import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Typography,
  Divider,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Box,
  CircularProgress
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import axios from 'axios';
import { getTimezoneLabel, displayDateTimeToUTC } from '../../../../utils/timezoneUtils';
import { formatDuration, formatClock, estimateHelicopterBaseFdpHours, buildSplitDutyEndReflection, deriveLastLandingTime, deriveEarliestNextDutyStart } from '../utils/crewDutyRosterUtils';
import {
  downloadAuthenticatedPdf,
  viewAuthenticatedPdf
} from '../../../../utils/authenticatedPdf';
import { useToast } from '../../../../contexts/ToastContext';

dayjs.extend(timezone);

const API_URL = process.env.REACT_APP_API_URL || '/api';

function idOf(ref) {
  if (!ref) return '';
  if (typeof ref === 'object' && ref._id) return String(ref._id);
  return String(ref);
}

function formatCharterOption(c) {
  if (!c) return '';
  const charter = c.charterNumber || c.tripTitle || 'Charter';
  const route = c.route || '';
  const times = [c.departureTime, c.arrivalTime].filter(Boolean).join('–');
  const seq = c.seqNo != null ? `S${c.seqNo}` : '';
  return [charter, seq, route, times].filter(Boolean).join(' · ');
}

/**
 * Create/Edit Dialog component for Crew Duty Roster
 */
const CrewDutyRosterDialog = ({
  dialogOpen,
  setDialogOpen,
  selectedEntry,
  formData,
  setFormData,
  aircraftType,
  setAircraftType,
  crewMembers,
  aircraftList = [],
  complianceContext,
  complianceViolations,
  complianceWarnings,
  handleSave,
  displayTimezone = 'Asia/Kolkata'
}) => {
  const { showToast } = useToast();
  const aircraftFilteredByType = (aircraftList || []).filter((a) => a.aircraftType === aircraftType);
  const showNonComplianceTab = selectedEntry?.isNonCompliant === true;
  const [dialogTab, setDialogTab] = useState(0);
  const [linkableCharters, setLinkableCharters] = useState([]);
  const [linkableLoading, setLinkableLoading] = useState(false);
  const [linkableError, setLinkableError] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);
  const tzLabel = getTimezoneLabel(displayTimezone);

  const openDutyPdf = async (download = false) => {
    if (!selectedEntry?._id) return;
    setPdfBusy(true);
    try {
      const url = `${API_URL}/ops/crew-duty-roster/${selectedEntry._id}/pdf${download ? '?download=1' : ''}`;
      const name = `duty-assignment-${selectedEntry._id}.pdf`;
      if (download) await downloadAuthenticatedPdf(url, name);
      else await viewAuthenticatedPdf(url);
    } catch (error) {
      showToast(
        typeof error?.message === 'string' && !error.message.startsWith('{') && !error.message.startsWith('%PDF')
          ? error.message
          : 'Failed to open duty PDF',
        'error'
      );
    } finally {
      setPdfBusy(false);
    }
  };

  useEffect(() => {
    if (dialogOpen || selectedEntry) {
      setDialogTab(0);
    }
  }, [dialogOpen, selectedEntry]);

  const fetchLinkableCharters = useCallback(async () => {
    if (!dialogOpen || !formData.crewId || !formData.dutyStartTime?.isValid?.() || !formData.dutyEndTime?.isValid?.()) {
      setLinkableCharters([]);
      return;
    }
    const startISO = displayDateTimeToUTC(
      formData.dutyStartTime.format('YYYY-MM-DD'),
      formData.dutyStartTime.format('HH:mm'),
      displayTimezone
    );
    const endISO = displayDateTimeToUTC(
      formData.dutyEndTime.format('YYYY-MM-DD'),
      formData.dutyEndTime.format('HH:mm'),
      displayTimezone
    );
    if (!startISO || !endISO) {
      setLinkableCharters([]);
      return;
    }

    setLinkableLoading(true);
    setLinkableError('');
    try {
      const params = new URLSearchParams({
        crewId: formData.crewId,
        dutyStartTime: startISO,
        dutyEndTime: endISO
      });
      const res = await axios.get(`${API_URL}/ops/crew-duty-roster/linkable-charters?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setLinkableCharters(res.data?.charters || []);
    } catch (err) {
      console.error('Error loading linkable charters:', err);
      setLinkableError(err.response?.data?.message || 'Failed to load overlapping charters');
      setLinkableCharters([]);
    } finally {
      setLinkableLoading(false);
    }
  }, [
    dialogOpen,
    formData.crewId,
    formData.dutyStartTime,
    formData.dutyEndTime,
    displayTimezone
  ]);

  useEffect(() => {
    fetchLinkableCharters();
  }, [fetchLinkableCharters]);

  const setDisplayTzDateTime = (field, newValue) => {
    if (!newValue) {
      setFormData({ ...formData, [field]: null });
      return;
    }
    const dateStr = newValue.format('YYYY-MM-DD');
    const timeStr = newValue.format('HH:mm');
    const d = dayjs.tz(`${dateStr}T${timeStr}`, displayTimezone);
    setFormData({ ...formData, [field]: d });
  };

  const selectedSectorId = idOf(formData.sectorId);
  const linkedCharterLabel = (() => {
    const fromList = linkableCharters.find((c) => c.sectorId === selectedSectorId);
    if (fromList) return formatCharterOption(fromList);
    if (selectedEntry?.tripId || selectedEntry?.sectorId) {
      const trip = typeof selectedEntry.tripId === 'object' ? selectedEntry.tripId : null;
      const sector = typeof selectedEntry.sectorId === 'object' ? selectedEntry.sectorId : null;
      const charter = trip?.charterNumber || trip?.title || null;
      const route =
        sector?.departure?.airport && sector?.arrival?.airport
          ? `${sector.departure.airport} → ${sector.arrival.airport}`
          : '';
      return [charter, route].filter(Boolean).join(' · ') || 'Linked charter';
    }
    return '';
  })();

  const handleCharterLinkChange = (sectorIdValue) => {
    if (!sectorIdValue) {
      setFormData({ ...formData, sectorId: null, tripId: null });
      return;
    }
    const match = linkableCharters.find((c) => c.sectorId === sectorIdValue);
    if (!match) return;
    setFormData({
      ...formData,
      sectorId: match.sectorId,
      tripId: match.tripId
    });
    if (match.aircraftType && (match.aircraftType === 'Helicopter' || match.aircraftType === 'Fixed Wing')) {
      setAircraftType(match.aircraftType);
    }
  };

  return (
    <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>{selectedEntry ? 'Edit Duty Entry' : 'Create Duty Entry'}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Times are entered and displayed in the display timezone ({tzLabel}). They are stored in UTC.
        </Alert>
        {showNonComplianceTab && (
          <Tabs value={dialogTab} onChange={(e, v) => setDialogTab(v)} sx={{ mb: 2 }}>
            <Tab label="Details" />
            <Tab label="Non-Compliance" />
          </Tabs>
        )}
        {(dialogTab === 0 || !showNonComplianceTab) && (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Crew Member</InputLabel>
              <Select
                value={formData.crewId}
                onChange={(e) => setFormData({ ...formData, crewId: e.target.value })}
                label="Crew Member"
              >
                {crewMembers.map((crew) => (
                  <MenuItem key={crew._id} value={crew._id}>
                    {crew.firstName} {crew.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Duty Type</InputLabel>
              <Select
                value={formData.dutyType}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData({
                    ...formData,
                    dutyType: v,
                    rosterPlanOnly: v === 'FLIGHT' ? formData.rosterPlanOnly : false,
                    dutyWorkLocationCity: v === 'FLIGHT' ? formData.dutyWorkLocationCity : '',
                    dutyWorkLocationCode: v === 'FLIGHT' ? formData.dutyWorkLocationCode : ''
                  });
                }}
                label="Duty Type"
              >
                <MenuItem value="FLIGHT">Flight</MenuItem>
                <MenuItem value="TRAINING">Training</MenuItem>
                <MenuItem value="STANDBY">Standby</MenuItem>
                <MenuItem value="RESERVE">Reserve</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
                <MenuItem value="SCHEDULED_DUTY_OFF">Scheduled off (plan)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {formData.dutyType === 'FLIGHT' && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.rosterPlanOnly === true}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setFormData({
                        ...formData,
                        rosterPlanOnly: on,
                        ...(!on ? { dutyWorkLocationCity: '', dutyWorkLocationCode: '' } : {})
                      });
                    }}
                  />
                }
                label="Roster plan only (full-day placeholder; skips compliance, excluded from flight-time totals)"
              />
            </Grid>
          )}
          {formData.dutyType === 'FLIGHT' && formData.rosterPlanOnly === true && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Work-Duty Location (city)"
                  value={formData.dutyWorkLocationCity}
                  onChange={(e) => setFormData({ ...formData, dutyWorkLocationCity: e.target.value })}
                  helperText="City where the pilot is assigned to work for this roster plan."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="City short code (4 letters)"
                  value={formData.dutyWorkLocationCode}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      dutyWorkLocationCode: e.target.value.toUpperCase().replace(/[^A-Za-z]/g, '').slice(0, 4)
                    })
                  }
                  inputProps={{ maxLength: 4 }}
                  placeholder="e.g. BENG"
                  helperText="Optional. If empty, first 4 letters of the city are shown (e.g. Bengaluru → Beng)."
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Aircraft Type</InputLabel>
              <Select
                value={aircraftType}
                onChange={(e) => {
                  setAircraftType(e.target.value);
                  if (formData.dutyType === 'TRAINING' && formData.trainingCategory === 'FLIGHT_TRAINING') {
                    setFormData({ ...formData, trainingAircraftId: '' });
                  }
                  if (e.target.value === 'Fixed Wing') {
                    setFormData({
                      ...formData,
                      crewComplement: 'SINGLE_PILOT',
                      numberOfLandings: 0,
                      hasPositioning: false,
                      isSplitDuty: false
                    });
                  }
                }}
                label="Aircraft Type"
              >
                <MenuItem value="Fixed Wing">Fixed Wing</MenuItem>
                <MenuItem value="Helicopter">Helicopter</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {formData.dutyType === 'TRAINING' && (
            <>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Training Category</InputLabel>
                  <Select
                    value={formData.trainingCategory || 'GROUND_TRAINING'}
                    onChange={(e) => setFormData({ ...formData, trainingCategory: e.target.value, trainingAircraftId: e.target.value === 'GROUND_TRAINING' ? '' : formData.trainingAircraftId })}
                    label="Training Category"
                  >
                    <MenuItem value="GROUND_TRAINING">Ground Training</MenuItem>
                    <MenuItem value="FLIGHT_TRAINING">Flight Training</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {formData.trainingCategory === 'FLIGHT_TRAINING' && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Aircraft (optional)</InputLabel>
                    <Select
                      value={formData.trainingAircraftId || ''}
                      onChange={(e) => setFormData({ ...formData, trainingAircraftId: e.target.value || '' })}
                      label="Aircraft (optional)"
                      displayEmpty
                    >
                      <MenuItem value="">None</MenuItem>
                      {aircraftFilteredByType.map((ac) => (
                        <MenuItem key={ac._id} value={ac._id}>
                          {ac.registration} {ac.model ? `(${ac.model})` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </>
          )}
          <Grid item xs={12} md={6}>
            <DateTimePicker
              label={`Duty Start Time (${tzLabel})`}
              value={formData.dutyStartTime ? dayjs(formData.dutyStartTime.format('YYYY-MM-DD HH:mm')) : null}
              onChange={(newValue) => setDisplayTzDateTime('dutyStartTime', newValue)}
              renderInput={(params) => (
                <TextField {...params} fullWidth helperText={`Enter time in ${tzLabel}`} />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DateTimePicker
              label={`Duty End Time (${tzLabel})`}
              value={formData.dutyEndTime ? dayjs(formData.dutyEndTime.format('YYYY-MM-DD HH:mm')) : null}
              onChange={(newValue) => setDisplayTzDateTime('dutyEndTime', newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  helperText={
                    formData.dutyStartTime?.isValid?.() && formData.dutyEndTime?.isValid?.() && formData.dutyEndTime.isAfter(formData.dutyStartTime)
                      ? `Enter time in ${tzLabel} · Duty length ${formatDuration(formData.dutyEndTime.diff(formData.dutyStartTime, 'minute') / 60)}`
                      : `Enter time in ${tzLabel}`
                  }
                />
              )}
            />
          </Grid>
          {formData.dutyStartTime?.isValid?.() && formData.dutyEndTime?.isValid?.() && formData.dutyEndTime.isAfter(formData.dutyStartTime) && (() => {
            const dutyHours = formData.dutyEndTime.diff(formData.dutyStartTime, 'minute') / 60;
            const lastLanding = deriveLastLandingTime(formData.dutyEndTime);
            const next = deriveEarliestNextDutyStart(formData.dutyEndTime, dutyHours, 12);
            return (
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Last landing (for the day):</strong>
                    {' '}
                    {formatClock(lastLanding, formData.dutyStartTime)}
                    {' '}(duty end is 30 min after landing)
                  </Typography>
                  <Typography variant="body2">
                    <strong>Earliest next duty start:</strong>
                    {' '}
                    {formatClock(next?.nextDutyStart, formData.dutyStartTime)}
                    {' '}(after {formatDuration(next?.restHours)} rest — max of duty length and 12h)
                  </Typography>
                </Alert>
              </Grid>
            );
          })()}
          {(formData.dutyType === 'FLIGHT' || formData.dutyType === 'TRAINING') && (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Link charter (optional)</InputLabel>
                <Select
                  value={selectedSectorId || ''}
                  label="Link charter (optional)"
                  onChange={(e) => handleCharterLinkChange(e.target.value)}
                  disabled={!formData.crewId || !formData.dutyStartTime || !formData.dutyEndTime || linkableLoading}
                >
                  <MenuItem value="">
                    <em>None — not linked to a charter</em>
                  </MenuItem>
                  {selectedSectorId &&
                    !linkableCharters.some((c) => c.sectorId === selectedSectorId) && (
                      <MenuItem value={selectedSectorId}>
                        {linkedCharterLabel || selectedSectorId} (current link)
                      </MenuItem>
                    )}
                  {linkableCharters.map((c) => (
                    <MenuItem key={c.sectorId} value={c.sectorId}>
                      {formatCharterOption(c)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {linkableLoading && <CircularProgress size={16} />}
                <Typography variant="caption" color="text.secondary">
                  Shows charters where this pilot is assigned and the sector overlaps the duty window.
                  FT Today still uses techlog BA→rotor stop, or charter schedule if no techlog — not the plan-block duration.
                </Typography>
              </Box>
              {linkableError && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {linkableError}
                </Alert>
              )}
              {!linkableLoading &&
                formData.crewId &&
                formData.dutyStartTime &&
                formData.dutyEndTime &&
                linkableCharters.length === 0 &&
                !linkableError && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    No overlapping assigned charters for this pilot in the selected duty window.
                  </Alert>
                )}
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Remarks"
              value={formData.remarks}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
            />
          </Grid>
          
          {/* Helicopter-Specific Fields */}
          {aircraftType === 'Helicopter' && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Helicopter-Specific Fields</Typography>
                </Divider>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Crew Complement</InputLabel>
                  <Select
                    value={formData.crewComplement}
                    onChange={(e) => setFormData({ ...formData, crewComplement: e.target.value })}
                    label="Crew Complement"
                  >
                    <MenuItem value="SINGLE_PILOT">Single Pilot</MenuItem>
                    <MenuItem value="TWO_PILOT">Two Pilot</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Number of Landings"
                  type="number"
                  value={formData.numberOfLandings}
                  onChange={(e) => setFormData({ ...formData, numberOfLandings: parseInt(e.target.value) || 0 })}
                  inputProps={{ min: 0, max: 10 }}
                  helperText="Required for two-pilot operations (affects FDP limits)"
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.hasPositioning}
                      onChange={(e) => setFormData({ ...formData, hasPositioning: e.target.checked })}
                    />
                  }
                  label="Has Positioning (Positioning time counts as duty time)"
                />
              </Grid>
              
              {formData.hasPositioning && (
                <>
                  <Grid item xs={12} md={6}>
                    <DateTimePicker
                      label={`Positioning Start Time (${tzLabel})`}
                      value={formData.positioningStartTime ? dayjs(formData.positioningStartTime.format('YYYY-MM-DD HH:mm')) : null}
                      onChange={(newValue) => setDisplayTzDateTime('positioningStartTime', newValue)}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth helperText={`Enter time in ${tzLabel}`} />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DateTimePicker
                      label={`Positioning End Time (${tzLabel})`}
                      value={formData.positioningEndTime ? dayjs(formData.positioningEndTime.format('YYYY-MM-DD HH:mm')) : null}
                      onChange={(newValue) => setDisplayTzDateTime('positioningEndTime', newValue)}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth helperText={`Enter time in ${tzLabel}`} />
                      )}
                    />
                  </Grid>
                  {formData.positioningStartTime && formData.positioningEndTime && (
                    <Grid item xs={12}>
                      <Alert severity="info">
                        Positioning Duration: {(formData.positioningEndTime.diff(formData.positioningStartTime, 'hour', true)).toFixed(1)} hours
                      </Alert>
                    </Grid>
                  )}
                </>
              )}
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isSplitDuty}
                      onChange={(e) => setFormData({ ...formData, isSplitDuty: e.target.checked })}
                    />
                  }
                  label="Split Duty (Allows FDP extension with break)"
                />
              </Grid>

              {!formData.isSplitDuty && formData.dutyStartTime?.isValid?.() && formData.dutyEndTime?.isValid?.() && formData.dutyEndTime.isAfter(formData.dutyStartTime) && (() => {
                const baseFdp = estimateHelicopterBaseFdpHours(formData.crewComplement, formData.numberOfLandings);
                const reflection = buildSplitDutyEndReflection({
                  dutyStart: formData.dutyStartTime,
                  dutyEnd: formData.dutyEndTime,
                  isSplitDuty: false,
                  baseFdpHours: baseFdp
                });
                if (!reflection) return null;
                return (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      <Typography variant="body2">
                        <strong>Without split duty:</strong>
                        {' '}entered end {formatClock(formData.dutyEndTime, formData.dutyStartTime)} ({formatDuration(reflection.dutyHours)})
                        {' · '}DGCA max ~{formatDuration(reflection.baseFdpHours)}
                        {' → latest end '}
                        {formatClock(reflection.maxEndWithout, formData.dutyStartTime)}
                        {reflection.withinWithout ? '' : ' — entered end exceeds base FDP'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Last landing {formatClock(reflection.lastLandingEntered, formData.dutyStartTime)}
                        {' · '}next duty from {formatClock(reflection.nextDutyStartEntered, formData.dutyStartTime)}
                        {' '}({formatDuration(reflection.restHoursEntered)} rest)
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Turn on Split Duty and set break times to extend the latest allowed duty end (half of break).
                      </Typography>
                    </Alert>
                  </Grid>
                );
              })()}
              
              {formData.isSplitDuty && (
                <>
                  <Grid item xs={12} md={6}>
                    <DateTimePicker
                      label={`Split Duty Break Start (${tzLabel})`}
                      value={formData.splitDutyBreakStart ? dayjs(formData.splitDutyBreakStart.format('YYYY-MM-DD HH:mm')) : null}
                      onChange={(newValue) => setDisplayTzDateTime('splitDutyBreakStart', newValue)}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth helperText={`Enter time in ${tzLabel}`} />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DateTimePicker
                      label={`Split Duty Break End (${tzLabel})`}
                      value={formData.splitDutyBreakEnd ? dayjs(formData.splitDutyBreakEnd.format('YYYY-MM-DD HH:mm')) : null}
                      onChange={(newValue) => setDisplayTzDateTime('splitDutyBreakEnd', newValue)}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth helperText={`Enter time in ${tzLabel}`} />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.splitDutyAccommodationProvided}
                          onChange={(e) => setFormData({ ...formData, splitDutyAccommodationProvided: e.target.checked })}
                        />
                      }
                      label="Accommodation Provided (Required if break > 6 hours or encroaches WOCL)"
                    />
                  </Grid>
                  {formData.splitDutyBreakStart && formData.splitDutyBreakEnd && (() => {
                    // Use whole minutes so 09:45→16:00 = 6h 15m (not 6.3h from toFixed(1))
                    const breakMinutes = formData.splitDutyBreakEnd.diff(formData.splitDutyBreakStart, 'minute');
                    const breakHours = breakMinutes / 60;
                    const extensionHours = breakHours / 2;
                    if (!(breakMinutes > 0)) {
                      return (
                        <Grid item xs={12}>
                          <Alert severity="error">
                            Break end must be after break start. Current break duration: {formatDuration(breakHours)}
                          </Alert>
                        </Grid>
                      );
                    }

                    const baseFdp = estimateHelicopterBaseFdpHours(
                      formData.crewComplement,
                      formData.numberOfLandings
                    );
                    const reflection = buildSplitDutyEndReflection({
                      dutyStart: formData.dutyStartTime,
                      dutyEnd: formData.dutyEndTime,
                      isSplitDuty: true,
                      breakStart: formData.splitDutyBreakStart,
                      breakEnd: formData.splitDutyBreakEnd,
                      baseFdpHours: baseFdp
                    });

                    return (
                      <Grid item xs={12}>
                        <Alert severity="info">
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Break Duration: {formatDuration(breakHours)} ({breakHours.toFixed(2)}h)
                            {' | '}
                            FDP Extension: {formatDuration(extensionHours)} ({extensionHours.toFixed(2)}h) — half of break
                          </Typography>
                          {reflection && (
                            <>
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                <strong>Duty end reflection</strong>
                                {' — '}
                                Entered end {formatClock(formData.dutyEndTime, formData.dutyStartTime)} ({formatDuration(reflection.dutyHours)})
                              </Typography>
                              {reflection.breakHours > 0 && reflection.segment1Hours >= 0 && (
                                <Typography variant="body2" color="text.secondary">
                                  Segments: {formatClock(formData.dutyStartTime)}–{formatClock(formData.splitDutyBreakStart)} ({formatDuration(reflection.segment1Hours)})
                                  {' + break '}
                                  {formatDuration(reflection.breakHours)}
                                  {' + '}
                                  {formatClock(formData.splitDutyBreakEnd)}–{formatClock(formData.dutyEndTime)} ({formatDuration(reflection.segment2Hours)})
                                </Typography>
                              )}
                              <Typography variant="body2" sx={{ mt: 0.75 }}>
                                <strong>Without split duty:</strong>
                                {' '}DGCA max ~{formatDuration(reflection.baseFdpHours)}
                                {' → latest end '}
                                {formatClock(reflection.maxEndWithout, formData.dutyStartTime)}
                                {reflection.withinWithout ? ' (entered end within base FDP)' : ' (entered end exceeds base FDP)'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Without split · last landing {formatClock(reflection.lastLandingWithout, formData.dutyStartTime)}
                                {' · '}next duty from {formatClock(reflection.nextDutyStartWithout, formData.dutyStartTime)}
                              </Typography>
                              <Typography variant="body2">
                                <strong>With split duty:</strong>
                                {' '}DGCA max ~{formatDuration(reflection.extendedFdpHours)}
                                {' (+'}
                                {formatDuration(reflection.extensionHours)}
                                {') → latest end '}
                                {formatClock(reflection.maxEndWith, formData.dutyStartTime)}
                                {reflection.withinWith ? ' (entered end within extended FDP)' : ' (entered end exceeds extended FDP)'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                With split · last landing {formatClock(reflection.lastLandingWith, formData.dutyStartTime)}
                                {' · '}next duty from {formatClock(reflection.nextDutyStartWith, formData.dutyStartTime)}
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 0.75 }}>
                                <strong>For entered duty end {formatClock(formData.dutyEndTime, formData.dutyStartTime)}:</strong>
                                {' '}last landing {formatClock(reflection.lastLandingEntered, formData.dutyStartTime)}
                                {' · '}earliest next duty {formatClock(reflection.nextDutyStartEntered, formData.dutyStartTime)}
                                {' '}({formatDuration(reflection.restHoursEntered)} rest)
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                Landing = duty end − 30 min. Next duty = duty end + max(duty length, 12h). Company policy and WOCL may further limit.
                              </Typography>
                            </>
                          )}
                        </Alert>
                      </Grid>
                    );
                  })()}
                </>
              )}
            </>
          )}
          
          {aircraftType && (
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Aircraft Type:</strong> {aircraftType} - Compliance checks are based on {aircraftType === 'Fixed Wing' ? 'D7J-J4-Fixed Wing.pdf' : 'D7J-J2 - Helicopter.pdf'} regulations
                </Typography>
              </Alert>
            </Grid>
          )}
          {complianceContext && (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Compliance Source:</strong> {complianceContext.isBaseline 
                    ? `Baseline Configuration (Default DGCA limits)` 
                    : `Active Profile: "${complianceContext.profileName}"`}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Aircraft Type:</strong> {complianceContext.aircraftType} ({complianceContext.regulationReference})
                </Typography>
              </Alert>
            </Grid>
          )}
          {complianceViolations.length > 0 && (
            <Grid item xs={12}>
              <Alert severity="error">
                <Typography variant="subtitle2" gutterBottom>Compliance Violations Detected:</Typography>
                <ul>
                  {complianceViolations.map((v, idx) => (
                    <li key={idx}>{v}</li>
                  ))}
                </ul>
              </Alert>
            </Grid>
          )}
          {complianceWarnings.length > 0 && (
            <Grid item xs={12}>
              <Alert severity="warning">
                <Typography variant="subtitle2" gutterBottom>Warnings:</Typography>
                <ul>
                  {complianceWarnings.map((w, idx) => (
                    <li key={idx}>{w.message || w}</li>
                  ))}
                </ul>
              </Alert>
            </Grid>
          )}
          {(complianceViolations.length > 0 || complianceWarnings.length > 0) && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Justification for Non-Compliance (Required, min 50 characters)"
                value={formData.nonComplianceJustification}
                onChange={(e) => setFormData({ ...formData, nonComplianceJustification: e.target.value })}
                required
                helperText={`${formData.nonComplianceJustification.length}/50 characters minimum`}
              />
            </Grid>
          )}
        </Grid>
        )}
        {showNonComplianceTab && dialogTab === 1 && (
          <Box sx={{ mt: 1 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This duty entry was saved with compliance violations. The justification provided at the time of save is shown below.
            </Alert>
            <TextField
              fullWidth
              multiline
              minRows={4}
              maxRows={12}
              label="Reason for non-compliance"
              value={selectedEntry?.nonComplianceJustification || ''}
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
        {selectedEntry?._id && (
          <>
            <Button onClick={() => openDutyPdf(false)} disabled={pdfBusy}>
              View PDF
            </Button>
            <Button onClick={() => openDutyPdf(true)} disabled={pdfBusy}>
              Download PDF
            </Button>
          </>
        )}
        <Button onClick={handleSave} variant="contained">
          {selectedEntry ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CrewDutyRosterDialog;
